import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AccountOnboardingView, GlobalAccount, GUID, PersonOrgType, Tenant } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';
import { findPersonDuplicates } from 'views/person/usePotentialPersonDuplicates';
import { Toast } from 'widgets';

export const AccountOnboardingQueryKeys = {
    newAccounts: ['account-onboarding', 'new-fidelity-accounts'] as const,
};

export const checkAccountOwnerDuplicates = async (account: AccountOnboardingView) => {
    const tenantGUID = account.tenantGUID;
    if (!tenantGUID) throw new Error('Account has no tenant assigned');
    const matches = await Promise.all(
        account.accountOwners
            .filter((owner) => owner.personOrgType === PersonOrgType.PERSON)
            .map((owner) =>
                findPersonDuplicates(tenantGUID, {
                    firstName: owner.firstName ?? undefined,
                    lastName: owner.lastName ?? undefined,
                    emails: [owner.primaryEmail].filter((value): value is string => !!value),
                    phones: [owner.primaryPhone, owner.workPhone].filter((value): value is string => !!value),
                    taxIdHash: owner.taxIdHash ?? undefined,
                }),
            ),
    );
    return [...new Map(matches.flat().map((person) => [person.personOrgGUID, person])).values()];
};

export const useListNewAccounts = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery<AccountOnboardingView[]>({
        queryKey: [...AccountOnboardingQueryKeys.newAccounts, 'tenants', tenantGUIDs],
        queryFn: async () => {
            const [response] = await CustomerApi().listNewAccountsForTenants(tenantGUIDs);
            return response ?? [];
        },
        enabled: tenantGUIDs.length > 0,
    });
};

export const useDismissAccountOnboarding = () => {
    const queryClient = useQueryClient();

    return useMutation<void, unknown, { accountNumber: string; tenantGUID: GUID<Tenant> }>({
        mutationFn: async ({ accountNumber, tenantGUID }) => {
            await CustomerApi().dismissAccountOnboardingForTenant(tenantGUID, accountNumber);
        },
        onSuccess: () => {
            Toast.success('Account dismissed from onboarding');
            queryClient.invalidateQueries({
                queryKey: AccountOnboardingQueryKeys.newAccounts,
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to dismiss account from onboarding');
        },
    });
};

export const useAssignAccountToHousehold = () => {
    const queryClient = useQueryClient();

    return useMutation<void | null, unknown, { accountNumber: string; householdGUID: GUID<GlobalAccount> }>({
        mutationFn: async ({ accountNumber, householdGUID }) => {
            const [response] = await CustomerApi().assignAccountToHousehold(accountNumber, householdGUID);
            return response;
        },
        onSuccess: () => {
            Toast.success('Account linked to household');
            queryClient.invalidateQueries({
                queryKey: AccountOnboardingQueryKeys.newAccounts,
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to link account to household');
        },
    });
};

export const useAssignAccountToHouseholdAsAdmin = () => {
    const queryClient = useQueryClient();

    return useMutation<void | null, unknown, { accountNumber: string; householdGUID: GUID<GlobalAccount> }>({
        mutationFn: async ({ accountNumber, householdGUID }) => {
            const [response] = await CustomerApi().assignAccountToHouseholdAsAdmin(accountNumber, householdGUID);
            return response;
        },
        onSuccess: () => {
            Toast.success('Account linked to household');
            queryClient.invalidateQueries({
                queryKey: AccountOnboardingQueryKeys.newAccounts,
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to link account to household');
        },
    });
};

export const useCreateHouseholdAndAssignAccount = () => {
    const queryClient = useQueryClient();

    return useMutation<
        GUID<GlobalAccount>,
        unknown,
        {
            tenantGUID: GUID<Tenant>;
            accountNumber: string;
            householdName: string;
            primaryAccountManagerOwnerKey: string;
        }
    >({
        mutationFn: async ({ tenantGUID, accountNumber, householdName, primaryAccountManagerOwnerKey }) => {
            const [response] = await CustomerApi().createHouseholdAndAssignAccount(tenantGUID, {
                accountNumber,
                householdName,
                primaryAccountManagerOwnerKey,
            });
            return response!;
        },
        onSuccess: () => {
            Toast.success('Household created and account linked');
            queryClient.invalidateQueries({
                queryKey: AccountOnboardingQueryKeys.newAccounts,
            });
        },
        onError: (error) => {
            Toast.forError(error, 'Failed to create household and link account');
        },
    });
};
